import { Injectable } from '@angular/core';
import { Subject, type Observable } from 'rxjs';
import { Metadata } from '@memberjunction/core';
import { CurrentUserRoleNames } from 'agu_app_shell';
import {
    BuildNav, BuildTasks, IsOEPPhase, OEP_ENTITY, OEP_LIVE_FILTER, ParseRoleResources, ParseSeasonTimeline, RoleKeysFromRoleNames,
    type IOEPRoleResources, type IOEPSeasonTimeline, type OEPNavItem, type OEPPhase, type OEPTask, type OEPUserSnapshot,
} from 'agu_oep_core';
import { OEPDataService } from '../data/oep-data.service';
import { BuildOEPSnapshot } from './oep-snapshot';
import { OEPCoalescedRunner } from './oep-coalesced-runner';
import { Str, StrOrNull } from '../views/shared/rows';

export interface OEPSeasonView { ID: string; Code: string; Name: string; Phase: OEPPhase; WinnersPerSection: number; Timeline: IOEPSeasonTimeline }
export interface OEPMe { UserID: string; FirstName: string; FullName: string; Initials: string }
export interface OEPParticipantView { ID: string; CareerStage: string | null; Institution: string | null }

export class OEPNoSeasonError extends Error {
    constructor() {
        super('There is no active OEP season yet.');
        this.name = 'OEPNoSeasonError';
    }
}

interface SeasonRow { ID: string; Code: string; Name: string; Phase: string; WinnersPerSection: number; Timeline: string | null; Resources: string | null }


/**
 * Everything the header, nav, Home and task list need, loaded once per surface as one
 * RunViews batch (OEPDataService.Batch) and re-loaded by Reload() after a write that changes
 * counts. Filters only narrow within RLS: each entity is already scoped to the signed-in user
 * server-side, so a query that does not apply to a role simply returns nothing for it.
 * Role membership comes from MJ's cached CurrentUser.UserRoles (CurrentUserRoleNames).
 */
@Injectable()
export class OEPContextService {
    private readonly changes = new Subject<void>();
    private season: OEPSeasonView | null = null;
    private snapshot: OEPUserSnapshot | null = null;
    private resources: IOEPRoleResources = {};
    private participant: OEPParticipantView | null = null;
    private me: OEPMe | null = null;
    private readonly loader = new OEPCoalescedRunner(() => this.run());

    constructor(private readonly data: OEPDataService) {}

    get Changes$(): Observable<void> { return this.changes.asObservable(); }
    get Season(): OEPSeasonView | null { return this.season; }
    get Snapshot(): OEPUserSnapshot | null { return this.snapshot; }
    get Resources(): IOEPRoleResources { return this.resources; }
    get Participant(): OEPParticipantView | null { return this.participant; }
    get Nav(): OEPNavItem[] { return this.snapshot ? BuildNav(this.snapshot) : []; }
    get Tasks(): OEPTask[] { return this.snapshot ? BuildTasks(this.snapshot) : []; }
    get HasAnyRole(): boolean { return (this.snapshot?.Roles.size ?? RoleKeysFromRoleNames(CurrentUserRoleNames()).size) > 0; }
    get IsStaff(): boolean { return this.snapshot?.Roles.has('staff') ?? false; }

    /** The signed-in user, read from MJ's cached CurrentUser once per load (templates read it several times per render). */
    get Me(): OEPMe {
        this.me ??= OEPContextService.ReadMe();
        return this.me;
    }

    private static ReadMe(): OEPMe {
        const u = new Metadata().CurrentUser;
        const first = u?.FirstName?.trim() || u?.Name?.trim().split(/\s+/)[0] || 'there';
        const full = u?.Name?.trim() || u?.Email?.trim() || 'Account';
        const initials = full.split(/\s+/).filter((p) => p.length > 0).slice(0, 2).map((p) => p[0].toUpperCase()).join('');
        return { UserID: u?.ID ?? '', FirstName: first, FullName: full, Initials: initials };
    }

    /** Loads the context; a call while a load is in flight joins it instead of racing it. */
    Load(): Promise<void> {
        return this.loader.Join();
    }

    /**
     * Re-loads after a write. While a load is in flight, one follow-up load is queued behind it (and
     * shared by every Reload that arrives meanwhile), so the result always reflects the write; the
     * follow-up takes over as the in-flight load with no gap (OEPCoalescedRunner).
     */
    Reload(): Promise<void> {
        return this.loader.Again();
    }

    private async run(): Promise<void> {
        this.me = null;
        const roles = RoleKeysFromRoleNames(CurrentUserRoleNames());
        if (roles.size === 0) {
            this.season = null;
            this.snapshot = null;
            this.participant = null;
            this.resources = {};
            this.changes.next();
            return;
        }
        const [seasonRow] = await this.data.Rows<SeasonRow>(OEP_ENTITY.Seasons, 'IsActive = 1', ['ID', 'Code', 'Name', 'Phase', 'WinnersPerSection', 'Timeline', 'Resources']);
        if (!seasonRow || !IsOEPPhase(seasonRow.Phase)) {
            this.season = null;
            this.snapshot = null;
            throw new OEPNoSeasonError();
        }
        const t = ParseSeasonTimeline(seasonRow.Timeline);
        if (!t.Ok) throw new Error(`The season timeline is misconfigured: ${t.Error}`);
        const res = ParseRoleResources(seasonRow.Resources);
        this.resources = res.Ok ? res.Value : {};
        const season: OEPSeasonView = {
            ID: seasonRow.ID, Code: seasonRow.Code, Name: seasonRow.Name, Phase: seasonRow.Phase,
            WinnersPerSection: Number(seasonRow.WinnersPerSection), Timeline: t.Value,
        };
        this.season = season;

        const me = OEPDataService.G(this.Me.UserID);
        const sid = OEPDataService.G(season.ID);
        const livePres = `SeasonID='${sid}' AND ${OEP_LIVE_FILTER.Presentation}`;
        const myLiveAssignments = `ReviewerUserID='${me}' AND ${OEP_LIVE_FILTER.Assignment}`;
        const [sections, myPres, participants, myFiles, released, notes, messages, assignments, assignedPres, myReviews, sessions, sessionPres, noms, committee, commNoms, slates] = await this.data.Batch([
            { EntityName: OEP_ENTITY.Sections, ExtraFilter: '1=1', Fields: ['ID', 'Name'] },
            // Oldest first: "my presentation" is MyPresentations[0] here and in the Presenter view (same order).
            { EntityName: OEP_ENTITY.Presentations, ExtraFilter: `${livePres} AND PresenterUserID='${me}'`, Fields: ['ID', 'Title', 'SectionID'], OrderBy: '__mj_CreatedAt, ID' },
            { EntityName: OEP_ENTITY.Participants, ExtraFilter: `SeasonID='${sid}' AND UserID='${me}'`, Fields: ['ID', 'CareerStage', 'Institution'] },
            { EntityName: OEP_ENTITY.Files, ExtraFilter: `ID IN (SELECT FileID FROM __mj.FileEntityRecordLink WHERE EntityID = (SELECT ID FROM __mj.Entity WHERE Name = '${OEP_ENTITY.Presentations}') AND TRY_CONVERT(UNIQUEIDENTIFIER, RecordID) IN (SELECT ID FROM FeedbackPlatform.Presentation WHERE PresenterUserID='${me}' AND SeasonID='${sid}'))`, Fields: ['ID', 'Category', 'Status'] },
            { EntityName: OEP_ENTITY.Reviews, ExtraFilter: `Status='Completed' AND ReviewAssignmentID IN (SELECT ra.ID FROM FeedbackPlatform.ReviewAssignment ra JOIN FeedbackPlatform.Presentation p ON p.ID=ra.PresentationID WHERE p.PresenterUserID='${me}' AND p.SeasonID='${sid}')`, Fields: ['ID', 'ReviewAssignmentID'] },
            { EntityName: OEP_ENTITY.ReviewThankYouNotes, ExtraFilter: '1=1', Fields: ['ReviewID'] },
            { EntityName: OEP_ENTITY.ConversationMessages, ExtraFilter: 'ThreadPosition IS NOT NULL', Fields: ['ConversationID', 'SenderUserID', 'ThreadPosition'] },
            { EntityName: OEP_ENTITY.ReviewAssignments, ExtraFilter: `${myLiveAssignments} AND PresentationID IN (SELECT ID FROM FeedbackPlatform.Presentation WHERE ${livePres})`, Fields: ['ID', 'PresentationID'] },
            { EntityName: OEP_ENTITY.Presentations, ExtraFilter: `${livePres} AND ID IN (SELECT PresentationID FROM FeedbackPlatform.ReviewAssignment WHERE ${myLiveAssignments})`, Fields: ['ID', 'SectionID'] },
            { EntityName: OEP_ENTITY.Reviews, ExtraFilter: `ReviewAssignmentID IN (SELECT ID FROM FeedbackPlatform.ReviewAssignment WHERE ReviewerUserID='${me}')`, Fields: ['ReviewAssignmentID', 'Status'] },
            { EntityName: OEP_ENTITY.SessionNominators, ExtraFilter: `UserID='${me}' AND SessionID IN (SELECT ID FROM FeedbackPlatform.[Session] WHERE SeasonID='${sid}')`, Fields: ['SessionID'] },
            { EntityName: OEP_ENTITY.Presentations, ExtraFilter: `${livePres} AND SessionID IN (SELECT SessionID FROM FeedbackPlatform.SessionNominator WHERE UserID='${me}')`, Fields: ['ID'] },
            { EntityName: OEP_ENTITY.Nominations, ExtraFilter: `SeasonID='${sid}' AND NominatorUserID='${me}' AND Status='Submitted'`, Fields: ['ID'] },
            { EntityName: OEP_ENTITY.CommitteeMembers, ExtraFilter: `SeasonID='${sid}' AND UserID='${me}'`, Fields: ['SectionID'] },
            { EntityName: OEP_ENTITY.Nominations, ExtraFilter: `SeasonID='${sid}' AND Status='Submitted' AND PresentationID IN (SELECT p.ID FROM FeedbackPlatform.Presentation p JOIN FeedbackPlatform.CommitteeMember m ON m.SeasonID=p.SeasonID AND m.SectionID=p.SectionID WHERE m.UserID='${me}')`, Fields: ['ID'] },
            // Visible only to the section's own committee (RLS, T5): a row here means "my section submitted".
            { EntityName: OEP_ENTITY.CommitteeSelections, ExtraFilter: `SeasonID='${sid}' AND Status='Submitted'`, Fields: ['ID'] },
        // Cross-role load: each role lacks Read on some of these entities by design (T5), so an unreadable one is empty.
        ], { UnreadableAsEmpty: true });

        const p0 = participants[0];
        this.participant = p0 ? { ID: Str(p0, 'ID'), CareerStage: StrOrNull(p0, 'CareerStage'), Institution: StrOrNull(p0, 'Institution') } : null;
        const fileRecords = await this.fileRecordIDs(myFiles.map((f) => Str(f, 'ID')));
        this.snapshot = BuildOEPSnapshot({
            Roles: roles,
            Me: me,
            Season: { Code: season.Code, Phase: season.Phase, Timeline: season.Timeline },
            Sections: sections.map((s) => ({ ID: Str(s, 'ID'), Name: Str(s, 'Name') })),
            MyPresentations: myPres.map((p) => ({ ID: Str(p, 'ID'), Title: Str(p, 'Title'), SectionID: StrOrNull(p, 'SectionID') })),
            MyFiles: myFiles.map((f) => ({ RecordID: fileRecords.get(Str(f, 'ID').toUpperCase()) ?? '', Category: Str(f, 'Category'), Status: Str(f, 'Status') })),
            ReleasedReviews: released.map((x) => ({ ID: Str(x, 'ID'), ReviewAssignmentID: Str(x, 'ReviewAssignmentID') })),
            MyThankYouNotes: notes.map((n) => ({ ReviewID: Str(n, 'ReviewID') })),
            ThreadMessages: messages.map((m) => ({
                ConversationID: Str(m, 'ConversationID'), SenderUserID: Str(m, 'SenderUserID'),
                ThreadPosition: m['ThreadPosition'] === null || m['ThreadPosition'] === undefined ? null : Number(m['ThreadPosition']),
            })),
            MyAssignments: assignments.map((a) => ({ ID: Str(a, 'ID'), PresentationID: Str(a, 'PresentationID') })),
            AssignedPresentations: assignedPres.map((p) => ({ ID: Str(p, 'ID'), SectionID: StrOrNull(p, 'SectionID') })),
            MyReviews: myReviews.map((x) => ({ ReviewAssignmentID: Str(x, 'ReviewAssignmentID'), Status: Str(x, 'Status') })),
            MySessions: sessions.map((s) => ({ ID: Str(s, 'SessionID') })),
            SessionPresentations: sessionPres.map((p) => ({ ID: Str(p, 'ID') })),
            MySubmittedNominations: noms.length,
            MyCommittee: committee[0] ? { SectionID: Str(committee[0], 'SectionID') } : null,
            CommitteeNominations: commNoms.length,
            CommitteeSlateSubmitted: slates.length > 0,
        });
        this.changes.next();
    }

    /** File ID (upper-case) → the Presentation ID it is linked to (MJ: File Entity Record Links). */
    private async fileRecordIDs(fileIDs: string[]): Promise<Map<string, string>> {
        if (fileIDs.length === 0) return new Map();
        const links = await this.data.Rows<{ FileID: string; RecordID: string }>(
            OEP_ENTITY.FileEntityRecordLinks, `FileID IN (${fileIDs.map((id) => `'${OEPDataService.G(id)}'`).join(',')})`, ['FileID', 'RecordID']);
        return new Map(links.map((l) => [l.FileID.toUpperCase(), l.RecordID]));
    }
}
